import React, { useState, useEffect, useCallback } from 'react';
import api, { API_URL } from '../../services/api';
import {
  DndContext, DragOverlay, useDraggable, useDroppable,
  PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  RefreshCw, X, UserPlus, Pause, Play, CheckCircle, Clock, Inbox, AlertCircle,
  GripVertical, Search, FileText,
} from 'lucide-react';
import Barcode from '../Common/Barcode';

// ── LOT 4 (12/06/2026) — Option A : "fiche a affecter" = l'OF entier.
// GAUCHE : 2 blocs — "A affecter" (1 carte/OF, code-barre N° OF, X etapes a
// planifier, draggable) + "En cours" (etapes actives a plat + chrono).
// Drop d'un OF sur un ouvrier -> mini-selecteur d'etape (/fabrication/<id>/etapes)
// -> planifier-etape (cree+affecte) ou /affecter si deja materialisee.
// DragOverlay (@dnd-kit), badges par statut, fallback bouton. Barcode non modifie.

const COULEURS = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-sky-500', 'bg-violet-500', 'bg-teal-500', 'bg-orange-500'];
const couleurOuvrier = (id) => COULEURS[(id || 0) % COULEURS.length];
const initiales = (o) => `${(o.prenom || '')[0] || ''}${(o.nom || '')[0] || ''}`.toUpperCase() || '?';
const STATUT_DONE = ['terminee', 'validee', 'termine'];
const STATUT_ACTIF = ['en_cours', 'pause', 'en_cours_externe'];

function chrono(dateDebut, now) {
  if (!dateDebut) return null;
  const sec = Math.max(0, Math.floor((now - new Date(dateDebut.replace(' ', 'T'))) / 1000));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(s).padStart(2, '0')}s`;
}

export default function AtelierKanban({ onOuvrirOF }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [scanHit, setScanHit] = useState(null);           // ordre_id repere par scan douchette (Entree)
  const [activeDrag, setActiveDrag] = useState(null);     // {type:'of'|'etape', data}
  const [selecteur, setSelecteur] = useState(null);       // {of, ouvrierId} -> modale choix etape
  const [selEtapes, setSelEtapes] = useState(null);       // etapes planifiables chargees
  const [selOuvrier, setSelOuvrier] = useState('');       // ouvrier choisi si pas de preset
  // #2 (12/06/2026) : saisie OPTIONNELLE du poids metal remis au demarrage
  const [selPoids, setSelPoids] = useState('');           // champ poids (modale selecteur)
  const [selPoidsInfo, setSelPoidsInfo] = useState('');   // origine de la suggestion
  // Fix regression selecteur (12/06 soir) : catalogue masque, dispo via "+ Ajouter"
  const [selCatalogue, setSelCatalogue] = useState([]);   // etapes catalogue (non_requis)
  const [showAjout, setShowAjout] = useState(false);
  const [etapeLibre, setEtapeLibre] = useState('');       // "autre" : description libre
  const [travailPrincipal, setTravailPrincipal] = useState(false); // OF sans etape + consigne -> bloc en haut

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const load = useCallback(async () => {
    try { const r = await api.get(`${API_URL}/atelier/kanban`); setData(r.data); }
    catch (e) { setError('Erreur chargement Kanban'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t1 = setInterval(() => setNow(Date.now()), 1000);
    const t2 = setInterval(load, 30000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [load]);

  const flash = (msg) => { setInfo(msg); setTimeout(() => setInfo(''), 5000); };

  // Affecter une etape DEJA materialisee (drop d'une carte etape, ou choix d'une etape en_attente)
  const affecter = async (etapeId, ouvrierId) => {
    setBusy(true); setError('');
    try {
      const r = await api.post(`${API_URL}/atelier/etapes/${etapeId}/affecter`, { ouvrier_id: parseInt(ouvrierId) });
      flash(r.data.message || 'Affectation réussie');
      await load();
    } catch (e) { setError(e.response?.data?.message || e.response?.data?.error || 'Erreur affectation'); }
    finally { setBusy(false); }
  };

  // #2 : enregistre le poids metal remis (g) au demarrage, APRES une affectation
  // reussie. Optionnel (vide = pas d'appel). Echec = on N'ANNULE PAS l'affectation.
  const putPoidsDonne = async (etapeId, poids) => {
    const val = parseFloat(poids);
    if (!etapeId || !poids || isNaN(val) || val <= 0) return;
    try { await api.put(`${API_URL}/atelier/etapes/${etapeId}/poids-or`, { poids_or_donne: val }); }
    catch (e) { setError('Affecté, mais poids non enregistré — ressaisissez-le dans la fiche OF.'); }
  };

  const action = async (etapeId, verbe, label) => {
    setBusy(true); setError('');
    try { await api.post(`${API_URL}/atelier/etapes/${etapeId}/${verbe}`); flash(label); await load(); }
    catch (e) { setError(e.response?.data?.error || `Erreur ${verbe}`); }
    finally { setBusy(false); }
  };

  // Ouvrir le mini-selecteur d'etape pour un OF (au drop sur un ouvrier OU via bouton)
  const ouvrirSelecteur = async (of, ouvrierId) => {
    setSelecteur({ of, ouvrierId: ouvrierId || null }); setSelOuvrier(''); setSelEtapes(null); setError('');
    setSelPoids(''); setSelPoidsInfo(''); setSelCatalogue([]); setShowAjout(false); setEtapeLibre('');
    try {
      const r = await api.get(`${API_URL}/atelier/fabrication/${of.ordre_id}/etapes`);
      // #2 : suggestion poids metal (meme cascade que preview-poids-or, deja dans la reponse)
      const d = r.data || {};
      const sugg = [
        [d.dernier_or_rendu, "or rendu à l'étape précédente"],
        [d.dernier_poids_bijou, 'dernier poids bijou mesuré'],
        [d.poids_or_devis, 'poids or du devis'],
        [d.poids_unitaire_of, "poids unitaire de l'OF"],
      ].find(([v]) => v != null && v !== '' && parseFloat(v) > 0);
      if (sugg) {
        const val = parseFloat(sugg[0]).toFixed(3);
        setSelPoids(val);
        setSelPoidsInfo(`suggéré : ${val} g — ${sugg[1]}`);
      }
      const ed = r.data?.etapes_devis || [];
      // Fix 12/06/2026 : l'import legacy (script 07) a cree des etapes nommees d'apres
      // l'OUVRIER (colonnes OUVRIER1-4 du xlsm), copiees par relancer_production.
      // On les exclut de la liste "a lancer" (match STRICT nom/prenom/"prenom nom",
      // jamais partiel — une vraie etape ne doit pas sauter sur un prenom courant).
      // Elles restent visibles dans Suivi Fiche / historique.
      const nomsOuvriers = new Set((data?.liste_ouvriers || []).flatMap(o =>
        [o.nom, o.prenom, `${o.prenom || ''} ${o.nom || ''}`]
          .filter(Boolean).map(s => s.trim().toLowerCase()).filter(s => s)));
      // Regression fix (12/06 soir) : le selecteur ne montre QUE les etapes REELLES
      // (devis dans l'ordre cf.ordre, ou etapes_production pour OF sans devis/SAV).
      // Le CATALOGUE (statut 'non_requis' / du_catalogue) est masque ici et propose
      // via "+ Ajouter une etape" — comme les vues existantes (Atelier.js §3747).
      const lancable = (e) =>
        !STATUT_DONE.includes(e.statut) && !STATUT_ACTIF.includes(e.statut) &&
        !nomsOuvriers.has((e.nom_etape || '').trim().toLowerCase());
      const estCatalogue = (e) => e.statut === 'non_requis' || e.du_catalogue === true;
      const reelles = ed.filter(e => lancable(e) && !estCatalogue(e));
      setSelEtapes(reelles);
      setSelCatalogue(ed.filter(e => lancable(e) && estCatalogue(e)));
      // OF sans étape réelle (SAV, import) : le TRAVAIL = consigne (colonne CONSIGNE
      // du SUIVI xlsm — description n'est que le nom de la pièce) devient le bloc
      // PRINCIPAL en haut de la modale (pré-rempli). Sans consigne : on auto-déplie
      // "+ Ajouter une étape" pour ne pas laisser un sélecteur vide.
      if (reelles.length === 0) {
        const trav = of.consigne || of.description || '';
        setEtapeLibre(trav);
        setTravailPrincipal(!!trav);
        if (!trav) setShowAjout(true);
      } else {
        setTravailPrincipal(false);
      }
    } catch (e) { setSelEtapes([]); setError('Erreur chargement des étapes'); }
  };

  // "+ Ajouter une etape" -> "Autre" : description libre (etapes-adhoc existant),
  // puis affectation + poids optionnel (#2) — meme chaine que choisirEtape.
  const ajouterEtapeLibre = async () => {
    const ouvrierId = selecteur.ouvrierId || selOuvrier;
    if (!ouvrierId) { setError('Choisir un ouvrier'); return; }
    if (!etapeLibre.trim()) { setError("Décrire l'étape"); return; }
    setBusy(true); setError('');
    try {
      const r1 = await api.post(`${API_URL}/atelier/ordres-fabrication/${selecteur.of.ordre_id}/etapes-adhoc`,
        { nom_etape: etapeLibre.trim(), type_ressource: 'interne', duree_estimee_heures: 1 });
      const eid = r1.data?.etape_production_id || r1.data?.id;
      await api.post(`${API_URL}/atelier/etapes/${eid}/affecter`, { ouvrier_id: parseInt(ouvrierId) });
      await putPoidsDonne(eid, selPoids);
      flash(`Étape « ${etapeLibre.trim()} » créée et affectée`);
      setSelecteur(null); setSelEtapes(null); await load();
    } catch (e) { setError(e.response?.data?.message || e.response?.data?.error || 'Erreur création étape'); }
    finally { setBusy(false); }
  };

  // Choix d'une etape dans le selecteur -> planifier (creer+affecter) ou affecter
  const choisirEtape = async (etape) => {
    const ouvrierId = selecteur.ouvrierId || selOuvrier;
    if (!ouvrierId) { setError('Choisir un ouvrier'); return; }
    setBusy(true); setError('');
    try {
      let resp;
      if (etape.id) {
        resp = await api.post(`${API_URL}/atelier/etapes/${etape.id}/affecter`, { ouvrier_id: parseInt(ouvrierId) });
      } else {
        resp = await api.post(`${API_URL}/atelier/ordres-fabrication/${selecteur.of.ordre_id}/planifier-etape`,
          { nom_etape: etape.nom_etape, ouvrier_id: parseInt(ouvrierId) });
      }
      const w = resp.data?.warning;
      // #2 : poids metal remis (optionnel) -> PUT poids-or avec l'etape_id renvoye
      // (planifier-etape ET /affecter renvoient tous deux etape_id)
      await putPoidsDonne(resp.data?.etape_id || etape.id, selPoids);
      flash(`Étape « ${etape.nom_etape} » affectée${w ? ' — ⚠️ ' + w : ''}`);
      setSelecteur(null); setSelEtapes(null); await load();
    } catch (e) { setError(e.response?.data?.message || e.response?.data?.error || 'Erreur affectation'); }
    finally { setBusy(false); }
  };

  const onDragStart = (ev) => setActiveDrag(ev.active?.data?.current || null);
  const onDragEnd = (ev) => {
    const drag = ev.active?.data?.current; setActiveDrag(null);
    const ouvrierId = ev.over?.id;
    if (!drag || !ouvrierId) return;
    if (drag.type === 'of') { ouvrirSelecteur(drag.of, ouvrierId); return; }
    if (drag.type === 'etape') {
      const e = drag.etape;
      if (e.ouvrier_affecte_id && parseInt(e.ouvrier_affecte_id) === parseInt(ouvrierId)) return;
      affecter(e.etape_id, ouvrierId);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400"><RefreshCw className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!data) return <div className="p-6 text-red-600">Impossible de charger le Kanban.</div>;

  // LOT 4-bis : panneau "A affecter" = 10 OF les plus recents par defaut (ordre_id
  // auto-increment DESC = derniers poses), recherche/scan douchette pour le reste.
  // Les OF termines/annules sont deja exclus cote backend (WHERE statut NOT IN).
  const q = recherche.trim().toLowerCase();
  const backlogTotal = [...(data.backlog || [])].sort((a, b) => (b.ordre_id || 0) - (a.ordre_id || 0));
  const matches = q
    ? backlogTotal.filter(o => [o.numero_ordre, o.ref_article, o.description, o.client_nom]
        .some(v => (v || '').toLowerCase().includes(q)))
    : backlogTotal;
  let backlog = q ? matches.slice(0, 50) : backlogTotal.slice(0, 10);
  // OF scanne : le faire remonter en tete (pret a glisser), meme hors des 10 recents
  if (scanHit) {
    const hit = backlogTotal.find(o => o.ordre_id === scanHit);
    if (hit) backlog = [hit, ...backlog.filter(o => o.ordre_id !== scanHit)];
  }

  // Scan douchette (lecteur USB = saisie clavier + Entree) : value = numero_ordre
  // tel qu'encode par Barcode.js. Tolerant : trim + insensible a la casse.
  const onScanEnter = () => {
    const norm = (s) => (s || '').trim().toLowerCase();
    const saisie = norm(recherche);
    if (!saisie) return;
    const exact = backlogTotal.find(o => norm(o.numero_ordre) === saisie);
    const cands = exact ? [exact] : backlogTotal.filter(o =>
      [o.numero_ordre, o.ref_article, o.description, o.client_nom].some(v => norm(v).includes(saisie)));
    if (cands.length === 1) {
      setScanHit(cands[0].ordre_id);
      flash(`OF ${cands[0].numero_ordre} trouvé — glissez-le sur un ouvrier`);
    } else if (cands.length === 0) {
      setError(`Aucun OF à affecter ne correspond à « ${recherche.trim()} » (terminé ou déjà tout affecté ?)`);
    }
  };

  const enCours = data.ouvriers.flatMap(o => (o.etapes || []).map(e => ({ ...e, _ouvrier: o })));

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">🛠 Atelier — qui fait quoi</h2>
          <div className="flex items-center gap-2">
            {info && <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded flex items-center gap-1"><CheckCircle className="h-3 w-3" />{info}</span>}
            <button onClick={load} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm flex items-center gap-1"><RefreshCw className="h-4 w-4" />Rafraîchir</button>
          </div>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center justify-between"><span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</span><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}
        <p className="text-xs text-gray-400 flex items-center gap-1"><GripVertical className="h-3 w-3" />Glissez une fiche OF sur un ouvrier (choix de l'étape ensuite) — ou bouton « Affecter ».</p>

        <div className="flex flex-col lg:flex-row gap-3">
          {/* ── GAUCHE : 2 blocs ── */}
          <div className="lg:w-[300px] lg:flex-shrink-0 space-y-3">
            {/* Bloc A : a affecter (1 carte / OF) */}
            <div className="bg-gray-50 rounded-xl border border-dashed border-gray-300 p-2.5 flex flex-col">
              <div className="flex items-center gap-2 mb-2 font-semibold text-gray-600 text-sm">
                <Inbox className="h-4 w-4" />Fiches à affecter
                <span className="ml-auto bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full" title={`${backlogTotal.length} OF au total`}>
                  {q ? `${matches.length} trouvé(s)` : `10 récents / ${backlogTotal.length}`}
                </span>
              </div>
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input value={recherche}
                  onChange={e => { setRecherche(e.target.value); setScanHit(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') onScanEnter(); }}
                  placeholder="📷 Scanner ou chercher un N° OF…"
                  className="w-full pl-7 pr-2 py-1.5 border rounded-lg text-xs" />
              </div>
              <div className="space-y-2 lg:max-h-[55vh] overflow-y-auto pr-1">
                {backlog.length === 0
                  ? <p className="text-xs text-gray-400 px-1 py-3 text-center">Aucune fiche.</p>
                  : backlog.map(o => <CarteOF key={o.ordre_id} of={o} highlight={o.ordre_id === scanHit} onAffecter={() => ouvrirSelecteur(o, null)} />)}
                {q && matches.length > 50 && <p className="text-xs text-gray-400 px-1 text-center">… {matches.length - 50} de plus (affinez la recherche)</p>}
                {!q && backlogTotal.length > 10 && <p className="text-xs text-gray-400 px-1 text-center">{backlogTotal.length - 10} autres OF — cherchez ou scannez pour les voir</p>}
              </div>
            </div>
            {/* Bloc B : en cours */}
            <div className="bg-white rounded-xl border shadow-sm p-2.5">
              <div className="flex items-center gap-2 mb-2 font-semibold text-gray-600 text-sm">
                <Play className="h-4 w-4 text-emerald-600" />En cours
                <span className="ml-auto bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">{enCours.length}</span>
              </div>
              <div className="space-y-1.5 max-h-[32vh] overflow-y-auto pr-1">
                {enCours.length === 0 ? <p className="text-xs text-gray-300 px-1 py-2 text-center">Rien en cours.</p>
                  : enCours.map(e => (
                    <div key={e.etape_id} className={`rounded-lg border p-2 text-xs ${e.statut === 'pause' ? 'border-amber-200 bg-amber-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-orange-700">{e.numero_ordre || `OF#${e.ordre_id}`}</span>
                        {e.statut === 'pause' ? <span className="text-amber-600 font-semibold">⏸</span>
                          : e.date_debut && <span className="flex items-center gap-1 text-emerald-700 font-semibold"><Clock className="h-3 w-3" />{chrono(e.date_debut, now)}</span>}
                      </div>
                      <p className="text-gray-700 truncate">{e.nom_etape} · {e._ouvrier.prenom} {e._ouvrier.nom}</p>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* ── DROITE : ouvriers en grille ── */}
          <div className="flex-1 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
            {data.ouvriers.map(o => (
              <ColonneOuvrier key={o.id} ouvrier={o} dragActif={!!activeDrag}>
                {(o.etapes || []).length === 0
                  ? <p className="text-xs text-gray-300 px-1 py-3 text-center">libre — déposez une fiche ici</p>
                  : o.etapes.map(e => (
                    <CarteEtape key={e.etape_id} e={e} now={now} busy={busy}
                      onPause={() => action(e.etape_id, 'pause', 'Étape mise en pause')}
                      onReprendre={() => action(e.etape_id, 'reprendre', 'Étape reprise')}
                      onTerminer={() => onOuvrirOF && onOuvrirOF(e.ordre_id)} />
                  ))}
              </ColonneOuvrier>
            ))}
          </div>
        </div>

        {/* Overlay de glisser */}
        <DragOverlay>
          {activeDrag?.type === 'of' && (
            <div className="rounded-lg border-2 border-indigo-400 bg-white p-2.5 shadow-lg w-60">
              <div className="font-mono text-xs font-bold text-orange-700">{activeDrag.of.numero_ordre}</div>
              <p className="text-xs text-gray-600">{activeDrag.of.nb_a_planifier} étape(s) à planifier</p>
            </div>
          )}
          {activeDrag?.type === 'etape' && (
            <div className="rounded-lg border-2 border-indigo-400 bg-white p-2 shadow-lg w-56">
              <div className="font-mono text-xs font-bold text-orange-700">{activeDrag.etape.numero_ordre}</div>
              <p className="text-xs text-gray-600 truncate">{activeDrag.etape.nom_etape}</p>
            </div>
          )}
        </DragOverlay>

        {/* Mini-sélecteur d'étape */}
        {selecteur && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] overflow-y-auto">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
                <h3 className="text-lg font-semibold">Quelle étape lancer ?</h3>
                <button onClick={() => { setSelecteur(null); setSelEtapes(null); }} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                  <span className="font-mono font-bold text-orange-700">{selecteur.of.numero_ordre}</span> — {selecteur.of.ref_article || selecteur.of.description || ''}
                </div>
                {!selecteur.ouvrierId && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Ouvrier *</label>
                    <select value={selOuvrier} onChange={e => setSelOuvrier(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">-- Choisir --</option>
                      {data.liste_ouvriers.map(o => <option key={o.id} value={o.id}>{o.prenom} {o.nom}{o.specialite ? ` (${o.specialite})` : ''}</option>)}
                    </select>
                  </div>
                )}
                {/* #2 : poids metal remis au demarrage — OPTIONNEL (etapes sans metal : laisser vide) */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">⚖️ Poids métal remis (g) — optionnel</label>
                  <input type="text" inputMode="decimal" value={selPoids}
                    onChange={e => setSelPoids(e.target.value.replace(',', '.'))}
                    onFocus={e => e.target.select()}
                    placeholder="laisser vide si pas de métal"
                    className="w-full px-3 py-2 border rounded-lg font-bold" />
                  {selPoidsInfo && <p className="text-xs text-amber-600 mt-0.5">{selPoidsInfo}</p>}
                </div>
                {/* OF sans étape réelle + consigne : le TRAVAIL est l'action PRINCIPALE, en haut */}
                {travailPrincipal ? (
                  <div className="border-2 border-amber-300 bg-amber-50/60 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-bold text-amber-700 uppercase">🛠 Travail à lancer</p>
                    <div className="flex gap-2">
                      <input type="text" value={etapeLibre} onChange={e => setEtapeLibre(e.target.value)}
                        className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm font-semibold" />
                      <button onClick={ajouterEtapeLibre} disabled={busy || !etapeLibre.trim()}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold disabled:opacity-50">
                        {busy ? '...' : 'Lancer'}
                      </button>
                    </div>
                    <p className="text-xs text-amber-600">Travail du registre — modifiable avant lancement.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-400">Cliquez sur l'étape à lancer{selecteur.ouvrierId ? '' : ' (après avoir choisi l\'ouvrier)'}.</p>
                    {selEtapes === null ? <p className="text-sm text-gray-400">Chargement…</p>
                      : selEtapes.length === 0 ? <p className="text-sm text-gray-400">Aucune étape prévue à lancer — utilisez « + Ajouter une étape » ci-dessous.</p>
                      : selEtapes.map((e, i) => (
                        <button key={i} onClick={() => choisirEtape(e)} disabled={busy}
                          className="w-full text-left px-3 py-2 border rounded-lg hover:bg-indigo-50 hover:border-indigo-300 flex items-center justify-between disabled:opacity-50">
                          <span className="text-sm text-gray-800">{e.nom_etape}</span>
                          {e.id
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">créée</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">📋 à planifier{(e.est_etape_devis || e.in_devis) ? ' (devis)' : ''}</span>}
                        </button>
                      ))}
                  </>
                )}

                {/* + Ajouter une (autre) etape : catalogue + description libre (secondaire) */}
                <div className="border-t pt-2">
                  <button onClick={() => setShowAjout(s => !s)}
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                    {showAjout ? '▾' : '▸'} + Ajouter une {travailPrincipal ? 'autre ' : ''}étape
                  </button>
                  {showAjout && (
                    <div className="mt-2 space-y-1.5">
                      {selCatalogue.length > 0 && (
                        <>
                          <p className="text-xs text-gray-400">Catalogue standard :</p>
                          {selCatalogue.map((e, i) => (
                            <button key={i} onClick={() => choisirEtape(e)} disabled={busy}
                              className="w-full text-left px-3 py-1.5 border border-dashed rounded-lg hover:bg-gray-50 flex items-center justify-between disabled:opacity-50">
                              <span className="text-sm text-gray-600">{e.nom_etape}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">catalogue</span>
                            </button>
                          ))}
                        </>
                      )}
                      {/* Champ libre seulement si le bloc principal n'est pas deja le champ libre */}
                      {!travailPrincipal && (
                        <>
                          <p className="text-xs text-gray-400 pt-1">Autre (description libre) :</p>
                          <div className="flex gap-2">
                            <input type="text" value={etapeLibre} onChange={e => setEtapeLibre(e.target.value)}
                              placeholder="ex. remise à la taille…"
                              className="flex-1 px-3 py-2 border rounded-lg text-sm" />
                            <button onClick={ajouterEtapeLibre} disabled={busy || !etapeLibre.trim()}
                              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                              {busy ? '...' : 'Lancer'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end">
                <button onClick={() => { setSelecteur(null); setSelEtapes(null); }} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Fermer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
}

// Type de pièce -> couleur, via la grille officielle T du numero_ordre + genre SAV.
// 1=bague · 2=boucles · 3=collier/pendentif · 4=bracelet · 5=autre · SAV (genre/is_sav).
function typePiece(of) {
  if (of.genre === 'SAV' || of.is_sav) return { label: 'SAV', bar: 'bg-rose-400', pill: 'bg-rose-100 text-rose-700' };
  const m = /OF-(\d)\./.exec(of.numero_ordre || '');
  const map = {
    '1': { label: 'Bague', bar: 'bg-indigo-400', pill: 'bg-indigo-100 text-indigo-700' },
    '2': { label: "Boucles d'oreilles", bar: 'bg-pink-400', pill: 'bg-pink-100 text-pink-700' },
    '3': { label: 'Collier / pendentif', bar: 'bg-sky-400', pill: 'bg-sky-100 text-sky-700' },
    '4': { label: 'Bracelet', bar: 'bg-amber-400', pill: 'bg-amber-100 text-amber-700' },
    '5': { label: 'Autre', bar: 'bg-gray-400', pill: 'bg-gray-100 text-gray-600' },
  };
  return map[m ? m[1] : '5'] || map['5'];
}
// Métier ouvrier affiché seulement si renseigné ET ≠ 'autre' (sinon masqué).
function metierOuvrier(o) {
  const s = (o.specialite || '').trim();
  return s && s.toLowerCase() !== 'autre' ? s : null;
}

// ── Carte OF « à affecter » (draggable + barre couleur par type + code-barre) ──
function CarteOF({ of, onAffecter, highlight }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `of-${of.ordre_id}`, data: { type: 'of', of } });
  const t = typePiece(of);
  return (
    <div ref={setNodeRef} className={`relative rounded-lg border bg-white p-2.5 pl-3 overflow-hidden ${isDragging ? 'opacity-40' : ''} ${highlight ? 'border-emerald-400 ring-2 ring-emerald-300 bg-emerald-50/40' : 'border-gray-200'}`}>
      <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${t.bar}`} aria-hidden="true" />
      <div className="flex items-start gap-1">
        <button {...listeners} {...attributes} className="touch-none cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 pt-0.5" title="Glisser sur un ouvrier" aria-label="Glisser">
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs font-bold text-orange-700">{of.numero_ordre || `OF#${of.ordre_id}`}</span>
            <span className={`text-[10px] px-1.5 rounded-full font-semibold ${t.pill}`}>{t.label}</span>
          </div>
          {of.numero_ordre && <div className="my-1"><Barcode value={of.numero_ordre} height={28} width={1.2} displayValue={false} /></div>}
          <p className="text-xs text-gray-800 font-medium truncate" title={of.description || of.ref_article || ''}>{of.description || of.ref_article || '—'}{of.client_nom ? ` · ${of.client_nom}` : ''}</p>
          {of.nb_a_planifier > 0 && (
            <p className="text-xs text-indigo-600 font-semibold flex items-center gap-1"><FileText className="h-3 w-3" />{of.nb_a_planifier} étape(s) à planifier</p>
          )}
          {/* OF sans étape réelle : le TRAVAIL (consigne xlsm) devient l'étape à lancer */}
          {!(of.nb_a_planifier > 0) && !(of.nb_etapes > 0) && (of.consigne || of.description) && (
            <p className="text-xs text-amber-700 font-semibold border border-dashed border-amber-300 bg-amber-50/60 rounded px-1.5 py-0.5 mt-0.5 truncate" title={of.consigne || of.description}>
              🛠 à lancer : {of.consigne || of.description}
            </p>
          )}
          <button onClick={onAffecter} className="mt-1.5 px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 flex items-center gap-1">
            <UserPlus className="h-3 w-3" />Affecter
          </button>
        </div>
      </div>
    </div>
  );
}

function ColonneOuvrier({ ouvrier: o, dragActif, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: o.id, data: { ouvrier: o } });
  const actif = (o.nb_en_cours > 0) || (o.nb_pause > 0);
  const metier = metierOuvrier(o);
  return (
    <div ref={setNodeRef}
      className={`rounded-xl border p-2.5 transition-colors ${isOver ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-300'
        : dragActif ? 'bg-white border-indigo-300 border-dashed'
        : actif ? 'bg-white border-emerald-200 shadow-sm' : 'bg-gray-50/60 border-gray-200 border-dashed'}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`h-8 w-8 rounded-full ${couleurOuvrier(o.id)} text-white flex items-center justify-center text-xs font-bold ${actif ? '' : 'opacity-60'}`}>{initiales(o)}</div>
        <div className="min-w-0">
          <p className="font-semibold text-sm text-gray-800 truncate">{o.prenom} {o.nom}</p>
          {metier && <p className="text-xs text-gray-400 truncate">{metier}</p>}
        </div>
        <div className="ml-auto flex gap-1">
          {o.nb_en_cours > 0 && <span className="bg-emerald-100 text-emerald-700 text-xs px-1.5 rounded-full font-semibold" title="en cours">{o.nb_en_cours}</span>}
          {o.nb_pause > 0 && <span className="bg-amber-100 text-amber-700 text-xs px-1.5 rounded-full font-semibold" title="en pause">{o.nb_pause}</span>}
        </div>
      </div>
      {actif
        ? <div className="space-y-2 max-h-[42vh] overflow-y-auto pr-0.5">{children}</div>
        : <div className="py-4 text-center text-xs text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">déposez une fiche ici</div>}
    </div>
  );
}

function CarteEtape({ e, now, busy, onPause, onReprendre, onTerminer }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `etape-${e.etape_id}`, data: { type: 'etape', etape: e } });
  const enPause = e.statut === 'pause';
  const enCours = e.statut === 'en_cours' || e.statut === 'en_cours_externe';
  return (
    <div ref={setNodeRef} className={`rounded-lg border p-2 bg-white ${isDragging ? 'opacity-40' : ''} ${enPause ? 'border-amber-300' : enCours ? 'border-emerald-300' : 'border-gray-200'}`}>
      <div className="flex items-center gap-1">
        <button {...listeners} {...attributes} className="touch-none cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500" title="Glisser vers un autre ouvrier" aria-label="Glisser">
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="font-mono text-xs font-bold text-orange-700">{e.numero_ordre || `OF#${e.ordre_id}`}</span>
        {enCours && e.date_debut && <span className="ml-auto flex items-center gap-1 text-xs text-emerald-700 font-semibold"><Clock className="h-3 w-3" />{chrono(e.date_debut, now)}</span>}
        {enPause && <span className="ml-auto text-xs text-amber-600 font-semibold">⏸ en pause</span>}
      </div>
      <p className="text-xs text-gray-800 mt-0.5 font-medium truncate pl-5" title={e.ref_article || e.description || ''}>{e.ref_article || e.description || '—'}</p>
      <p className="text-xs text-gray-500 pl-5">Étape {e.numero_etape} · {e.nom_etape || '—'}</p>
      <div className="flex flex-wrap gap-1 mt-1.5 pl-5">
        {enCours && <button onClick={onPause} disabled={busy} className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 flex items-center gap-1"><Pause className="h-3 w-3" />Pause</button>}
        {enPause && <button onClick={onReprendre} disabled={busy} className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 flex items-center gap-1"><Play className="h-3 w-3" />Reprendre</button>}
        <button onClick={onTerminer} title="Terminer dans la fiche (suivi or/pierres)" className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-1"><CheckCircle className="h-3 w-3" />Terminer</button>
      </div>
    </div>
  );
}
